import React from 'react';

import useQuery from '@smart-tv/hooks/use-query';

import { TvGuideChannel } from '@smart-tv/pages/tv-guide/types';

export interface SelectedDate {
  date: Date;
  isToday: boolean;
}

export interface TvGuideQueryParams {
  genre?: string;
  channel?: string;
  date?: string;
}

export interface TvGuideContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export interface TvGuideContextValues {
  channels?: TvGuideChannel[];
  setChannels: React.Dispatch<React.SetStateAction<TvGuideChannel[] | undefined>>;

  selectedDate: SelectedDate;
  setSelectedDate: React.Dispatch<React.SetStateAction<SelectedDate>>;

  calendarDialog: boolean;
  setCalendarDialog: React.Dispatch<React.SetStateAction<boolean>>;

  favoritesDialog: boolean;
  setFavoritesDialog: React.Dispatch<React.SetStateAction<boolean>>;

  pendingScrollToLive: boolean;
  setPendingScrollToLive: React.Dispatch<React.SetStateAction<boolean>>;

  scrollToCurrentTimeAction: (() => Promise<void>) | null;
  setScrollToCurrentTimeAction: React.Dispatch<React.SetStateAction<(() => Promise<void>) | null>>;

  savedTimelinePosition: number | null;
  setSavedTimelinePosition: React.Dispatch<React.SetStateAction<number | null>>;

  isChannelsLoading: boolean;
  setIsChannelsLoading: React.Dispatch<React.SetStateAction<boolean>>;

  resetViewStates: () => void;

  dateChanged: boolean;
  setDateChanged: React.Dispatch<React.SetStateAction<boolean>>;

  favoritesChanged: boolean;
  setFavoritesChanged: React.Dispatch<React.SetStateAction<boolean>>;
}

export const TvGuideContext = React.createContext<TvGuideContextValues>({} as TvGuideContextValues);

export const TvGuideConsumer = TvGuideContext.Consumer;

export const TvGuideProvider = (props: TvGuideContextProps): JSX.Element => {
  const { children } = props;

  const { date } = useQuery<TvGuideQueryParams>();

  const [channels, setChannels] = React.useState<TvGuideChannel[]>();

  const [selectedDate, setSelectedDate] = React.useState<SelectedDate>({
    date: date ? new Date(date) : new Date(),
    isToday: !date
  });

  const [calendarDialog, setCalendarDialog] = React.useState(false);
  const [favoritesDialog, setFavoritesDialog] = React.useState(false);

  const [dateChanged, setDateChanged] = React.useState(false);
  const [favoritesChanged, setFavoritesChanged] = React.useState(false);

  const [pendingScrollToLive, setPendingScrollToLive] = React.useState(false);
  const [scrollToCurrentTimeAction, setScrollToCurrentTimeAction] = React.useState<
    (() => Promise<void>) | null
  >(null);

  const [savedTimelinePosition, setSavedTimelinePosition] = React.useState<number | null>(null);
  const [isChannelsLoading, setIsChannelsLoading] = React.useState(true);

  const resetViewStates = React.useCallback(() => {
    setDateChanged(false);
    setFavoritesChanged(false);
  }, []);

  return (
    <TvGuideContext.Provider
      value={{
        channels,
        setChannels,
        selectedDate,
        setSelectedDate,
        calendarDialog,
        setCalendarDialog,
        favoritesDialog,
        setFavoritesDialog,
        pendingScrollToLive,
        setPendingScrollToLive,
        scrollToCurrentTimeAction,
        setScrollToCurrentTimeAction,
        savedTimelinePosition,
        setSavedTimelinePosition,
        isChannelsLoading,
        setIsChannelsLoading,
        resetViewStates,
        dateChanged,
        setDateChanged,
        favoritesChanged,
        setFavoritesChanged
      }}
    >
      {children}
    </TvGuideContext.Provider>
  );
};

export default TvGuideContext;
